import { z } from "zod";
import { AnnouncementBannerSchema } from "#cards/announcementBanner.js";
import { JustShippedCardSchema } from "#cards/justShippedCard.js";
//#region src/flags/flags.ts
let Flag = /* @__PURE__ */ function(Flag) {
	/**
	* Show activity logs page.
	*/
	Flag["ActivityLogs"] = "activity_logs";
	/**
	* Gate the activity log webhook subscriptions feature (create/list/get/update/
	* delete/test). When disabled, the webapp-api tRPC procedures return 404 so the
	* feature is invisible to ungated teams.
	*/
	Flag["ActivityLogWebhooks"] = "activity_log_webhooks";
	/**
	* Show the redesigned billing settings page.
	*/
	Flag["BillingRedesign"] = "billing_redesign";
	/**
	* Show the redesigned Gas Sponsorship experience. When on, the gas-manager
	* mount points render the new `features/gas-sponsorship/` tree instead of the
	* legacy `features/gas-policies/` experience.
	*/
	Flag["GasSponsorshipRedesignEnabled"] = "gas_sponsorship_redesign_enabled";
	/**
	* Show the dark mode toggle in the header.
	*/
	Flag["ShowThemeToggle"] = "show_theme_toggle";
	Flag["ProductTimeseriesCharts"] = "product_timeseries_charts";
	/**
	* Show the disable API key toggle in app JWT settings.
	*/
	Flag["ShowDisableApiKeyToggle"] = "show_disable_api_key_toggle";
	/**
	* Enable IP range allowlisting with CIDR notation support.
	*/
	Flag["WhitelistIpRanges"] = "whitelist_ip_ranges";
	/**
	* Show the team-level IP allowlist UI.
	*/
	Flag["ShowTeamIpAllowlist"] = "show_team_ip_allowlist";
	/**
	* Show network and method filters for error alerts.
	*/
	Flag["ShowMethodAndNetworkInErrorAlert"] = "show_method_and_network_in_error_alert";
	/**
	* Show Admin APIs option in access key creation.
	*/
	Flag["AdminAccessKeys"] = "admin_access_keys";
	/**
	* Enable role-based access control checks for team permissions.
	*/
	Flag["Rbac"] = "rbac";
	/**
	* Show the Wallet API Logs tab alongside Request Logs on the Logs page.
	*/
	Flag["TransactionLifecycleDashboard"] = "transaction_lifecycle_dashboard";
	/**
	* Gate Tenderly debugging integration for Wallet API Logs (direct debugger
	* links for onchain failures and simulation links for offchain failures).
	*/
	Flag["TenderlyDebug"] = "tenderly_debug";
	/**
	* Show the Wallets tab in the nav.
	* Targets team IDs via the Statsig gate "show_wallets".
	*/
	Flag["ShowWallets"] = "show_wallets";
	/**
	* Enables the refreshed pay-as-you-go upgrade modal experience.
	*/
	Flag["UpgradeModalRefresh"] = "upgrade_modal_refresh";
	/**
	* Gates the API key rotation feature in the Dashboard UI and the App
	* Management API. Backend reads this server-side to reject ungated
	* rotation requests — frontend gating alone is insufficient.
	*/
	Flag["ApiKeyRotationEnabled"] = "api_key_rotation_enabled";
	/**
	* Gates the in-Dashboard AI assistant end-to-end (entry point, side panel,
	* and assistant endpoint). Defaults off; staged rollout is driven from the
	* Statsig gate "enable_dashboard_ai_assistant". Backend reads this
	* server-side so the assistant endpoint stays inert for ungated teams —
	* frontend gating alone is insufficient.
	*/
	Flag["EnableDashboardAiAssistant"] = "enable_dashboard_ai_assistant";
	/**
	* Show the CLI promo banner and per-chart CLI command snippets on the Usage
	* page.
	*/
	Flag["UsageCliPromo"] = "usage_cli_promo";
	/**
	* Routes throughput/usage queries to the `dx-throughput` cube for the
	* caller's team (DX-3126), same effect as the staff-only
	* x-use-experimental-data-source header override. Backend reads this
	* server-side in usageRouter so the rollout can be widened per-team ahead
	* of removing the header override.
	*/
	Flag["EnableThroughputCube"] = "enable_throughput_cube";
	Flag["WebhookCreationTemplate"] = "webhook_creation_template";
	/**
	* Show the persistent CU usage widget in the nav sidebar footer (PAYG
	* experiment, DX-3007).
	*/
	Flag["PersistentCuUsageWidget"] = "persistent_cu_usage_widget";
	/**
	* Assigns a user/team to a specific chain analytics dashboard by slug.
	*/
	Flag["ChainAnalyticsAssignment"] = "chain_analytics_assignment";
	/**
	* Grants admin access to all chain analytics dashboards.
	*/
	Flag["ChainAnalyticsAdmin"] = "chain_analytics_admin";
	Flag["AnnouncementBanner"] = "announcement_banner";
	Flag["FeaturedChains"] = "featured_chains";
	Flag["JustShippedCards"] = "just_shipped_cards";
	Flag["OutageBanner"] = "outage_banner";
	Flag["ScheduleCallPopover"] = "schedule_call_popover";
	Flag["WalletServices"] = "wallet_services";
	Flag["GlobalBanner"] = "global_banner";
	/**
	* Enables the custom JWT provider for social auth.
	*/
	Flag["CustomJwtProvider"] = "custom_jwt_provider";
	return Flag;
}({});
const FlagEnum = z.nativeEnum(Flag);
const UnvalidatedNetworkIdsSchema = z.object({ networkIds: z.array(z.string()) });
const FLAG_SCHEMA_MAP = {
	["activity_logs"]: z.boolean(),
	["activity_log_webhooks"]: z.boolean(),
	["billing_redesign"]: z.boolean(),
	["gas_sponsorship_redesign_enabled"]: z.boolean(),
	["show_theme_toggle"]: z.boolean(),
	["product_timeseries_charts"]: z.boolean(),
	["show_disable_api_key_toggle"]: z.boolean(),
	["whitelist_ip_ranges"]: z.boolean(),
	["show_team_ip_allowlist"]: z.boolean(),
	["show_method_and_network_in_error_alert"]: z.boolean(),
	["admin_access_keys"]: z.boolean(),
	["rbac"]: z.boolean(),
	["transaction_lifecycle_dashboard"]: z.boolean(),
	["tenderly_debug"]: z.boolean(),
	["show_wallets"]: z.boolean(),
	["upgrade_modal_refresh"]: z.boolean(),
	["api_key_rotation_enabled"]: z.boolean(),
	["enable_dashboard_ai_assistant"]: z.boolean(),
	["usage_cli_promo"]: z.boolean(),
	["enable_throughput_cube"]: z.boolean(),
	["webhook_creation_template"]: z.object({ is_test: z.boolean() }),
	["persistent_cu_usage_widget"]: z.boolean(),
	["chain_analytics_assignment"]: z.object({ chainSlug: z.string() }),
	["chain_analytics_admin"]: z.boolean(),
	["featured_chains"]: z.object({
		featuredChainIds: z.array(z.string()),
		popularChainIds: z.array(z.string())
	}),
	["just_shipped_cards"]: z.object({ cards: z.array(JustShippedCardSchema) }),
	["announcement_banner"]: z.object({ banners: z.array(AnnouncementBannerSchema) }),
	["outage_banner"]: z.object({
		enabled: z.boolean(),
		message: z.string()
	}),
	["schedule_call_popover"]: z.object({
		ctaText: z.string(),
		ctaUrl: z.string().url(),
		description: z.string(),
		openPopoverText: z.string(),
		enabled: z.boolean()
	}),
	["wallet_services"]: z.object({ gasManager: UnvalidatedNetworkIdsSchema }),
	["global_banner"]: z.object({
		contentMd: z.string(),
		variant: z.enum([
			"info",
			"warning",
			"error"
		])
	}),
	["custom_jwt_provider"]: z.boolean()
};
const flagSchemaMap = z.object(FLAG_SCHEMA_MAP);
const FLAG_DEFAULTS = {
	["activity_logs"]: false,
	["activity_log_webhooks"]: false,
	["billing_redesign"]: false,
	["gas_sponsorship_redesign_enabled"]: false,
	["show_theme_toggle"]: false,
	["product_timeseries_charts"]: false,
	["custom_jwt_provider"]: false,
	["show_disable_api_key_toggle"]: false,
	["whitelist_ip_ranges"]: false,
	["show_team_ip_allowlist"]: false,
	["show_method_and_network_in_error_alert"]: false,
	["admin_access_keys"]: false,
	["rbac"]: false,
	["transaction_lifecycle_dashboard"]: false,
	["tenderly_debug"]: false,
	["show_wallets"]: false,
	["upgrade_modal_refresh"]: false,
	["api_key_rotation_enabled"]: false,
	["enable_dashboard_ai_assistant"]: false,
	["usage_cli_promo"]: false,
	["enable_throughput_cube"]: false,
	["webhook_creation_template"]: { is_test: false },
	["persistent_cu_usage_widget"]: false,
	["chain_analytics_assignment"]: { chainSlug: "" },
	["chain_analytics_admin"]: false,
	["announcement_banner"]: { banners: [] },
	["featured_chains"]: {
		featuredChainIds: [],
		popularChainIds: []
	},
	["just_shipped_cards"]: { cards: [] },
	["outage_banner"]: {
		enabled: false,
		message: ""
	},
	["schedule_call_popover"]: {
		ctaText: "",
		ctaUrl: "",
		description: "",
		openPopoverText: "",
		enabled: false
	},
	["wallet_services"]: { gasManager: { networkIds: [] } },
	["global_banner"]: {
		contentMd: "",
		variant: "info"
	}
};
//#endregion
export { FLAG_DEFAULTS, FLAG_SCHEMA_MAP, Flag, FlagEnum, flagSchemaMap };

//# sourceMappingURL=flags.js.map